import { Box, Stack } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useFieldArray } from 'react-hook-form';

import { useFormFieldsDisabled } from '@/components/form/context/formFieldsDisabled.context';
import { SortableList } from '@/components/lists/DragAndDropList/SortableList';
import { useFormContext } from '@/components/react-hook-form';
import { SortableRecipientItem } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsFormRecipients/SortableRecipientItem';
import { DispositiveProvisionTemplateKind, EntityKind } from '@/types/schema';

import { DISPOSITIVE_PROVISIONS_FORM_NAMESPACE } from '../../DispositiveProvisionsForm/DispositiveProvisionsForm.constants';
import {
  DispositiveProvisionsFormPaths,
  DispositiveProvisionsFormShape,
  RecipientKind,
} from '../../DispositiveProvisionsForm/DispositiveProvisionsForm.types';
import { DraggableListItemRecipient } from '../../DispositiveProvisionsForm/DispositiveProvisionsFormRecipients/DraggableListItemRecipient';
import {
  useRecipientOptions,
  useRecipientOptionsParams,
} from '../../DispositiveProvisionsForm/hooks/useRecipientOptions';
import { DispositiveProvisionsTemplateEditorTabHeader } from '../DispositiveProvisionsTemplateSplitScreenModal.components';

export function DispositiveProvisionsTemplatePouroverWillPreset() {
  const [forceExpandId, setForceExpandId] = useState<string | null>(null);
  const { control } = useFormContext<DispositiveProvisionsFormShape>();
  const { disabled: disabledFromContext } = useFormFieldsDisabled();

  // Get entity data to determine which recipients are RevocableTrusts
  const recipientOptionsParams = useRecipientOptionsParams();
  const { entityRecipients } = useRecipientOptions(recipientOptionsParams);

  // Track when a new recipient is added to auto-expand it
  const hasUnhandledAddition = useRef(false);

  const {
    append,
    remove,
    move,
    fields: recipients,
  } = useFieldArray({
    control,
    name: `${DISPOSITIVE_PROVISIONS_FORM_NAMESPACE}.recipients` as const satisfies DispositiveProvisionsFormPaths,
    keyName: '_id',
  });

  // Determine if a recipient is a RevocableTrust (used for default entity naming)
  const isRevocableTrustRecipient = useCallback(
    (recipientId: string | null, recipientKind: RecipientKind | null) => {
      if (!recipientId || recipientKind !== RecipientKind.Entity) {
        return false;
      }
      const entity = entityRecipients.find((e) => e.id === recipientId);
      return entity?.kind === EntityKind.RevocableTrust;
    },
    [entityRecipients]
  );

  useEffect(() => {
    if (!hasUnhandledAddition.current) return;
    const mostRecentlyAddedRecipient = recipients[recipients.length - 1];
    if (mostRecentlyAddedRecipient) {
      setForceExpandId(mostRecentlyAddedRecipient._id);
    }
    hasUnhandledAddition.current = false;
  }, [recipients, recipients.length]);

  // Wrap append to track when recipients are added for auto-expansion
  const handleAddRecipient: typeof append = useCallback(
    (...args) => {
      append(...args);
      hasUnhandledAddition.current = true;
    },
    [append]
  );

  const handleRemoveRecipient = useCallback(
    (index: number) => {
      remove(index);
    },
    [remove]
  );

  const handleReorder = useCallback(
    (oldIndex: number, newIndex: number) => {
      move(oldIndex, newIndex);
    },
    [move]
  );

  return (
    <Stack spacing={2}>
      <DispositiveProvisionsTemplateEditorTabHeader
        templateType={DispositiveProvisionTemplateKind.PouroverWill}
        recipients={recipients}
        onAddTemplateRecipient={handleAddRecipient}
      />
      <SortableList
        items={recipients}
        getId={(r) => r._id}
        onReorder={handleReorder}
        disabled={disabledFromContext}
      >
        <Box component="ul" sx={{ p: 0, m: 0, listStyleType: 'none' }}>
          {recipients.map((r, i) => {
            const isFirstRecipient = i === 0;
            const isRevTrust = isRevocableTrustRecipient(
              r.recipientId,
              r.recipientKind
            );

            return (
              <SortableRecipientItem
                key={r._id}
                id={r._id}
                disabled={disabledFromContext}
              >
                {(Draghandle) => (
                  <DraggableListItemRecipient
                    // First recipient cannot be removed; additional recipients can
                    onRemove={
                      isFirstRecipient ? null : () => handleRemoveRecipient(i)
                    }
                    defaultExpanded={
                      forceExpandId === r._id || isFirstRecipient
                    }
                    index={i}
                    Draghandle={Draghandle}
                    templateType={DispositiveProvisionTemplateKind.Default}
                    // First recipient shows "Select trust" placeholder to guide initial selection
                    emptyRecipientOptionDisplay={
                      isFirstRecipient ? 'Select trust' : undefined
                    }
                    recipientConfiguration={
                      // Set default entity name for RevocableTrust pourover recipients
                      isRevTrust
                        ? { defaultNewEntityName: 'Pourover will' }
                        : undefined
                    }
                  />
                )}
              </SortableRecipientItem>
            );
          })}
        </Box>
      </SortableList>
    </Stack>
  );
}
